import { useEffect, useState } from 'react';
import config from '../config';
import { getSandbox, planFeature, planHiddenTabs } from '../services/SandboxService';
import { getSettings } from '../services/SettingsService';
import { connectionState } from '../components/sandbox/connections';
import { modelChoices } from '../components/sandbox/modelChoices';
import { isTabHidden } from '../utils/projectTabsModel';

/**
 * What the person's plan includes, for the places that only need to hide or explain a feature (sandbox-first
 * experience, docs/SANDBOX_FIRST.md): the playbook picker, the playbook launcher, Home's playbook auto-pick and the
 * chat's plan notice.
 *
 * Read from the sandbox overview (`GET /api/v1/sandbox`, `plan.features`) once per page and shared, asked again at
 * most once a minute — the pages that show the overview itself keep using `useSandbox`. Without
 * `config.sandboxFirst`, or while the overview says nothing (an older vfs-service, not loaded yet, failed), every
 * feature counts as included: today's Flow.
 *
 * `aiFoundry` is whether the AI Foundry connection is on for the person: off when the overview's
 * `connections.aiFoundry` says it is not enabled by the organization or not in their plan, or when the person chose
 * their own model key (settings `onboarding.keySource: "own"`). Off, Flow asks AI Foundry for nothing (no Catalog
 * tools, playbooks, specs: utils/aiFoundryGate.js), Chat runs on the person's own key and Flow's default playbook.
 * An overview that does not say (an older vfs-service) leaves it on: today's Flow.
 *
 * `hiddenTabs` are the project tabs the plan takes away (`plan.tabs.hidden`, utils/projectTabsModel.js): none without
 * sandbox-first or while the overview says nothing.
 */
export const PLAN_FEATURES_EVENT = 'flow:plan-features-changed';
const CHANGE_EVENT = PLAN_FEATURES_EVENT;
const STALE_MS = 60000;

const NO_TABS = Object.freeze([]);
let overview = null;
// The last hidden-tab list, kept while it says the same: a stable value for the hooks' dependencies.
let lastHidden = NO_TABS;
function hiddenTabsOf(sandbox) {
  const next = planHiddenTabs(sandbox);
  if (next.join(',') !== lastHidden.join(',')) { lastHidden = next.length ? Object.freeze(next) : NO_TABS; }
  return lastHidden;
}
let keySource = null;
let settled = false;
let loading = null;
let askedAt = 0;

function notify() {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** Asks the sandbox for the overview (shared by every caller; at most once a minute). */
export function loadPlanFeatures() {
  if (!config.sandboxFirst) { return Promise.resolve(); }
  if (!loading || Date.now() - askedAt > STALE_MS) {
    askedAt = Date.now();
    loading = Promise.all([
      getSandbox()
        .then((next) => { overview = next; })
        .catch(() => { /* Unknown: everything stays included. */ }),
      getSettings()
        .then((settings) => { keySource = settings?.onboarding?.keySource ?? null; })
        .catch(() => { /* Unknown: the organization's models, as before. */ }),
    ]).finally(() => { settled = true; notify(); });
  }
  return loading;
}

function read() {
  const on = Boolean(config.sandboxFirst);
  return {
    // Whether the answer is in: right away without sandbox-first, else once the sandbox answered (or failed).
    ready: !on || settled,
    planName: on ? overview?.plan?.name || null : null,
    playbooks: !on || planFeature(overview, 'playbooks') !== false,
    chatOrgModels: !on || planFeature(overview, 'chatOrgModels') !== false,
    chatOwnKey: !on || planFeature(overview, 'chatOwnKey') !== false,
    aiFoundry: !on || aiFoundryOn(overview, keySource),
    // Whether the plan includes Flow's chat at all: it runs on the organization's models or on the person's own key,
    // and the plan can leave out both (`plan.features.chatOrgModels` / `chatOwnKey`). There is no `chat` flag of its
    // own: chat is in the plan when either way to run it is.
    chat: !on || chatAvailableIn(overview),
    // Chat runs on the person's own key: they chose it, or the organization's models are not offered to them.
    ownKeyChat: on && ownKeyChat(overview, keySource),
    hiddenTabs: on ? hiddenTabsOf(overview) : NO_TABS,
    // The web terminal (the Terminal tab, the Code view's dock): on unless the plan says false. The server refuses the
    // socket anyway; per project, hooks/useTerminalTab.js asks the terminal endpoint (the owner's plan, the role).
    terminal: !on || planFeature(overview, 'terminal') !== false,
  };
}

/**
 * Whether Flow's chat can run for the person with this sandbox overview: the organization's models (provided by the
 * deployment and in the plan) or their own key (in the plan). Unknown (no overview yet, an older vfs-service) counts
 * as included, as every feature here.
 */
export function chatAvailableIn(sandbox) {
  if (!sandbox?.plan?.features) { return true; }
  const { org, own } = modelChoices(sandbox);
  return org.available || own.available;
}

/** Whether the person's plan includes Flow's chat, as last read (`true` while unknown). */
export function chatInPlan() {
  return read().chat;
}

/** Off when the organization or the plan leaves AI Foundry out, or the person chose their own key; else on. */
function aiFoundryOn(sandbox, source) {
  const state = connectionState(sandbox, 'aiFoundry');
  if (state === 'org-off' || state === 'not-in-plan') { return false; }
  return source !== 'own';
}

function ownKeyChat(sandbox, source) {
  // The organization's models are the default whenever they are offered (deployment and plan); the AI Foundry
  // connection does not decide the models, only AI Foundry's playbooks and tools.
  const orgOffered = Boolean(config.chatOrgModels) && planFeature(sandbox, 'chatOrgModels') !== false;
  const ownAllowed = planFeature(sandbox, 'chatOwnKey') !== false;
  return (source === 'own' && ownAllowed) || !orgOffered;
}

/** Whether the AI Foundry connection is on for the person, as last read (`true` while unknown). */
export function aiFoundryInUse() {
  return read().aiFoundry;
}

/** Asks again on the next read (the person just changed something the answer depends on, e.g. saved their key). */
export function refreshPlanFeatures() {
  askedAt = 0;
  loading = null;
  return loadPlanFeatures();
}

/** Whether playbooks are in the person's plan, as last read (`true` while unknown). */
export function playbooksInPlan() {
  return read().playbooks;
}

/** "your Free plan", or "your plan" when the overview has no name. */
export function planLabel(planName) {
  return planName ? `your ${planName} plan` : 'your plan';
}

/** Forgets what was loaded (tests). */
export function resetPlanFeatures() {
  overview = null;
  keySource = null;
  settled = false;
  loading = null;
  askedAt = 0;
  lastHidden = NO_TABS;
}

/**
 * `(tab) => boolean`: whether the person's plan shows a project tab (or view: `changes` goes with History), for the
 * links into it from elsewhere ("See in Data", "Open in API"), which are left out when it does not.
 */
export function usePlanTab() {
  const { hiddenTabs } = usePlanFeatures();
  return (tab) => !isTabHidden(hiddenTabs, tab);
}

/** `{ ready, planName, playbooks, chatOrgModels, chatOwnKey, aiFoundry, chat, ownKeyChat, hiddenTabs, terminal }`, kept in sync across components. */
export default function usePlanFeatures() {
  const [features, setFeatures] = useState(read);
  useEffect(() => {
    const sync = () => setFeatures(read());
    window.addEventListener(CHANGE_EVENT, sync);
    loadPlanFeatures();
    sync();
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);
  return features;
}
